import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { NotificationService } from './service'
import type { NotificationItem } from './types'

export const useNotifications = defineStore('notifications', () => {
  const notifications = ref<NotificationItem[]>([])
  const selectedNotification = ref<NotificationItem>()
  const loading = ref<boolean>(false)

  const unreadNotificationsCount = computed(() => {
    return notifications.value.filter((item) => !item.read).length
  })

  const hasUnreadNotifications = computed(() => {
    return unreadNotificationsCount.value > 0
  })

  async function getNotifications(accountId: string) {
    loading.value = true

    try {
      const response = await NotificationService.list(accountId)

      if (response?.data) {
        notifications.value = response.data
      }

      return response
    } finally {
      loading.value = false
    }
  }

  async function setNotificationRead(
    accountId: string,
    notificationId: string,
    read: boolean
  ) {
    loading.value = true

    try {
      const response = await NotificationService.setRead(
        accountId,
        notificationId,
        read
      )

      const index = notifications.value.findIndex(
        (item) => item.id === notificationId
      )

      if (index >= 0) {
        notifications.value[index] = {
          ...notifications.value[index],
          read,
        }
      }

      if (selectedNotification.value?.id === notificationId) {
        selectedNotification.value = {
          ...selectedNotification.value,
          read,
        }
      }

      return response
    } finally {
      loading.value = false
    }
  }

  async function setAllNotificationsRead(accountId: string, read: boolean) {
    loading.value = true

    try {
      const response = await NotificationService.setAllRead(accountId, read)

      notifications.value = notifications.value.map((item) => ({
        ...item,
        read,
      }))

      if (selectedNotification.value) {
        selectedNotification.value = {
          ...selectedNotification.value,
          read,
        }
      }

      return response
    } finally {
      loading.value = false
    }
  }

  async function readNotification(accountId: string, notificationId: string) {
    return await setNotificationRead(accountId, notificationId, true)
  }

  async function unreadNotification(accountId: string, notificationId: string) {
    return await setNotificationRead(accountId, notificationId, false)
  }

  async function readAllNotifications(accountId: string) {
    return await setAllNotificationsRead(accountId, true)
  }

  async function unreadAllNotifications(accountId: string) {
    return await setAllNotificationsRead(accountId, false)
  }

  function setSelectedNotification(notification?: NotificationItem) {
    selectedNotification.value = notification
  }

  function resetSelectedNotification() {
    selectedNotification.value = undefined
  }

  return {
    loading,
    notifications,
    selectedNotification,
    unreadNotificationsCount,
    hasUnreadNotifications,
    getNotifications,
    setNotificationRead,
    setAllNotificationsRead,
    readNotification,
    unreadNotification,
    readAllNotifications,
    unreadAllNotifications,
    setSelectedNotification,
    resetSelectedNotification,
  }
})
